/**
 * Types shared by the YOM-backed analytics UI (project Analytics page and the
 * superadmin Platform insights page).
 *
 * Backend contract: YOM-EVERYWHERE-ARCHITECTURE.md §7 ("Tyashin backend") and,
 * once T2 lands it, `aapastech-backend/docs/analytics-embed-api.md`. The
 * normalizers in `lib/api.ts` (analyticsEmbedHelpers) accept both snake_case
 * and camelCase so a naming drift on the backend does not blank the page.
 */

/** Report names `<yom-report>` understands. */
export type YomReportName =
  | 'overview'
  | 'timeseries'
  | 'breakdown'
  | 'ecommerce'
  | 'funnel'
  | 'realtime';

/** Everything a `<yom-report>` needs besides the report attributes. */
export interface EmbedSource {
  token: string;
  /** YOM project id the token is bound to. */
  project: string;
  /** YOM API base, e.g. https://api.yourownmetrics.aapastech.com */
  api: string;
}

/**
 * One fetch from the backend can mint several tokens (Platform insights mints
 * one for T-APP and one for T-SITES). The bundle expires when its earliest
 * token does.
 */
export interface TokenBundle {
  /** Epoch ms. */
  expiresAt: number;
  sources: Record<string, EmbedSource>;
  /** Optional extras the backend may send (funnel ids for platform insights). */
  meta?: Record<string, string>;
}

/** GET /projects/:id/analytics/status (normalized; analytics-embed-api.md §1). */
export interface ProjectAnalyticsStatus {
  /** YOM_MANAGED_ORGS is on in this environment. */
  managedOrgs: boolean;
  /** The analytics plugin row exists / is enabled. */
  installed: boolean;
  enabled: boolean;
  provisioned: boolean;
  /** 'managed-org' is the only strategy that renders YOM embeds. */
  strategy: string | null;
  /** True once the owner has claimed the YOM org. */
  claimed: boolean;
  /** Server's answer to "may this user claim?" (owner/admin). Null = not sent. */
  canClaim: boolean | null;
  /** YOM project id, for the deep link after claim. */
  yomProjectId: string | null;
  /** Explicit deep link into YOM, if the backend ever sends one. */
  dashboardUrl: string | null;
  /** ISO time the site started sending events (the auto-install time). */
  collectingSince: string | null;
  /** ISO time of the first event ever received, if the backend sends it. */
  firstEventAt: string | null;
  /** Public URL of the site, if the backend sends it (else derived from the project). */
  siteUrl: string | null;
  /** ISO 4217, if the backend sends it (else the store currency). */
  currency: string | null;
}

/** Managed orgs are on but this site has no usable YOM binding yet. */
export function isSettingUp(status: ProjectAnalyticsStatus | null | undefined): boolean {
  return Boolean(status?.managedOrgs && !status.provisioned);
}

export const MANAGED_ORG_STRATEGY = 'managed-org';

/** True when the page should render the YOM-backed experience. */
export function usesYomEmbeds(status: ProjectAnalyticsStatus | null | undefined): boolean {
  return Boolean(status?.provisioned && status.strategy === MANAGED_ORG_STRATEGY);
}
